import { useCallback, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { useAtomValue } from 'jotai';
import { isNil } from 'shared/src/utils';
import { useLogAnalyticsEvent } from 'shared/src/hooks';
import type { MarketRankingItem } from 'shared/src/api';
import { marketRankingFilterAtom } from '../states/marketRankingFilter.atom';

type ListItem =
  | { type: 'filter' }
  | { type: 'market'; data: MarketRankingItem };

export const useLogDisplayMarket = () => {
  const loggedMarketSnosRef = useRef<Set<number>>(new Set());
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const filter = useAtomValue(marketRankingFilterAtom);

  // logApplyFilter 가 호출되는 시점은 곧 marketRankingFilterAtom 이 갱신되는 시점.
  // 필터가 바뀌면 동일 마켓도 새 필터 컨텍스트에서 다시 DISPLAY_MARKET 이 발화되어야
  // 하므로 dedup cache 를 비운다. (logApplyFilter 호출처와 본 hook 이 다른 컴포넌트라
  // ref 공유 대신 atom 변화 감지로 자동 처리.)
  useEffect(() => {
    loggedMarketSnosRef.current.clear();
  }, [filter]);

  return useCallback<
    NonNullable<FlatListProps<ListItem>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable) return;
        if (item.type !== 'market') return;

        const market = item.data;
        if (loggedMarketSnosRef.current.has(market.sno)) return;
        if (isNil(market.market_type?.name)) return;

        loggedMarketSnosRef.current.add(market.sno);

        logAnalyticsEvent({
          channel: ['ablylog', 'firebase'],
          name: 'DISPLAY_MARKET',
          analytics: {
            SCREEN: 'MARKET_RANKING',
            MARKET_SNO: market.sno.toString(),
            MARKET_TYPE: market.market_type.name,
          },
        });
      });
    },
    [logAnalyticsEvent],
  );
};
